class ProductGridContainer extends HTMLElement {
	constructor() {
		super()
		this.selectors = {
			container: '#ProductGridContainer',
			paginate: '[data-paginate]',
			btnLoadmore: '[data-load-more]',
			productGrid: '[data-products-grid]',
			columnSelector: '.column-switcher button',
		}
		this.restoreStateMaxAge = 30 * 60 * 1000
	}

	connectedCallback() {
		this.init()
		this.onPageHide = this.saveScrollState.bind(this)
		window.addEventListener('pagehide', this.onPageHide)
	}

	disconnectedCallback() {
		if (this.infiniteLoadingObserver && this.domNodes && this.domNodes.btnLoadmore) {
			this.infiniteLoadingObserver.unobserve(this.domNodes.btnLoadmore)
		}
		if (this.onPageHide) {
			window.removeEventListener('pagehide', this.onPageHide)
		}
	}

	init() {
		this.container = this.closest(this.selectors.container)
		this.sectionId = this.dataset.sectionId
		this.domNodes = queryDomNodes(this.selectors, this)
		this.infiniteLoadingObserver = null
		this.currentPage = 1
		this.isLoading = false
		this.isRestoring = false
		this.storageKey = this.getStorageKey()
		this.save_key = 'foxtheme:grid-columns'

		this.initLoadMore()
		this.restoreLoadedProducts()
		this.columnSwitcher()
	}

	initLoadMore() {
		const {paginate} = this.domNodes
		if (!paginate) return
		this.paginateType = paginate.dataset.paginate
		this.totalPages = Number(paginate.dataset.totalPages)

		if (this.paginateType === 'number' || this.totalPages <= 1) return

		const { btnLoadmore } = this.domNodes
		addEventDelegate({
			context: this,
			selector: this.selectors.btnLoadmore,
			handler: e => {
				e.preventDefault()
				this.loadMoreProducts()
			}
		})

		if (this.paginateType === 'infinite') {
			this.infiniteLoadingObserver = new IntersectionObserver((entries) => {
				entries.forEach(entry => {
					if (entry.intersectionRatio === 1 && !this.isRestoring) this.loadMoreProducts()
				});
			}, { threshold: 1 });

			this.infiniteLoadingObserver.observe(btnLoadmore)
		}
	}

	//TODO: Re-check after filter
	loadMoreProducts() {
		if (this.isLoading || this.isRestoring) return

		const btnLoadmore = this.querySelector(this.selectors.btnLoadmore)
		const nextPage = this.currentPage + 1
		if (nextPage > this.totalPages) return

		this.isLoading = true
		this.toggleLoading(true, btnLoadmore)
		this.appendProductsFromPage(nextPage)
			.then(() => {
				this.currentPage = nextPage
				this.saveScrollState()
				this.updatePagination(nextPage)
			})
			.catch(err => console.error(`Failed to load more products.`, err))
			.finally(() => {
				this.toggleLoading(false, btnLoadmore)
				this.isLoading = false
			})
	}

	appendProductsFromPage(page) {
		const productGrid = this.querySelector(this.selectors.productGrid)
		if (!productGrid) return Promise.resolve()

		return fetchSection(this.sectionId, { fromCache: true, params: { page } })
			.then(html => {
				const productNodes = html.querySelectorAll('[data-products-grid] .f-column:not(.custom-block-wrapper)')
				productNodes.forEach(prodNode => productGrid.appendChild(prodNode))
			})
	}

	restoreLoadedProducts() {
		const state = this.getSavedScrollState()
		if (!state || !this.totalPages || state.page <= 1 || !this.shouldRestoreScrollState()) return

		const targetPage = Math.min(state.page, this.totalPages)
		let previousScrollRestoration
		this.isRestoring = true
		this.toggleLoading(true, this.domNodes.btnLoadmore)

		if ('scrollRestoration' in history) {
			previousScrollRestoration = history.scrollRestoration
			history.scrollRestoration = 'manual'
		}

		let restoreQueue = Promise.resolve()
		for (let page = 2; page <= targetPage; page += 1) {
			restoreQueue = restoreQueue.then(() => this.appendProductsFromPage(page))
		}

		restoreQueue
			.then(() => {
				this.currentPage = targetPage
				this.updatePagination(targetPage)
				return new Promise(resolve => {
					requestAnimationFrame(() => {
						window.scrollTo(0, state.scrollY || 0)
						resolve()
					})
				})
			})
			.catch(err => console.error(`Failed to restore loaded products.`, err))
			.finally(() => {
				this.isRestoring = false
				this.toggleLoading(false, this.domNodes.btnLoadmore)
				if (previousScrollRestoration) {
					history.scrollRestoration = previousScrollRestoration
				}
			})
	}

	updatePagination(page) {
		const btnLoadmore = this.querySelector(this.selectors.btnLoadmore)
		if (!btnLoadmore || page < this.totalPages) return

		btnLoadmore.parentNode.remove()
		if (this.infiniteLoadingObserver) {
			this.infiniteLoadingObserver.unobserve(btnLoadmore)
		}
	}

	getStorageKey() {
		return `foxtheme:collection-grid:${window.location.pathname}${window.location.search}`
	}

	getSavedScrollState() {
		try {
			const state = JSON.parse(sessionStorage.getItem(this.storageKey))
			if (!state || Date.now() - state.timestamp > this.restoreStateMaxAge) return null
			return state
		} catch (error) {
			return null
		}
	}

	shouldRestoreScrollState() {
		const navigation = performance.getEntriesByType('navigation')[0]
		if (navigation && navigation.type === 'back_forward') return true

		try {
			return new URL(document.referrer).pathname.includes('/products/')
		} catch (error) {
			return false
		}
	}

	saveScrollState() {
		if (!this.storageKey || this.currentPage <= 1) return

		try {
			sessionStorage.setItem(this.storageKey, JSON.stringify({
				page: this.currentPage,
				scrollY: window.scrollY,
				timestamp: Date.now(),
			}))
		} catch (error) {
			return
		}
	}

	toggleLoading(loading, btnLoadmore) {
		if (btnLoadmore) {
			const method = loading ? 'add' : 'remove'
			btnLoadmore.classList[method]('btn--loading')
		}
	}

	columnSwitcher() {
		const data = localStorage.getItem(this.save_key)
		if (!window.FoxThemeSettings.designMode && '1,2'.includes(data)) this.setColumn(data)

		addEventDelegate({
			selector: this.selectors.columnSelector,
			context: this,
			handler: (e, target) => {
				e.preventDefault()
				const {column} = target.dataset
				this.setColumn(column)
			}
		})

	}

	setColumn(column) {
		const productGrid = document.querySelector(this.selectors.productGrid)
		if (!productGrid) return
		const target = this.querySelector('.column-switcher button[data-column="'+ column +'"]')
		const selected = this.querySelector('.column-switcher button[aria-selected="true"]')
		if (selected) {
			selected.removeAttribute('aria-selected')
		}
		target.setAttribute('aria-selected', true)

		if (column === '2') {
			productGrid.classList.add('f-grid-2-cols');
			productGrid.classList.remove('f-grid-1-cols');
		} else {
			productGrid.classList.remove('f-grid-2-cols');
			productGrid.classList.add('f-grid-1-cols');
		}

		localStorage.setItem(this.save_key, column)
	}
}

customElements.define('product-grid-container', ProductGridContainer)
